import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface HistoryRow {
  readonly historyId: string;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly eventType: string;
  readonly fromStatus: string | null;
  readonly toStatus: string;
  readonly occurredAt: string;
  readonly remarks: string | null;
}

interface JobSummary {
  readonly jobNumber: string | null;
  readonly customerName: string | null;
}

export function BoAssignmentHistoryPage(): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { jobRequestId } = useParams<{ jobRequestId: string }>();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly HistoryRow[] | null>(null);
  const [job, setJob] = useState<JobSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await apiRequest<readonly HistoryRow[]>(`/v1/vendor-assignments/history/${jobRequestId}`, { token });
      setRows(r);
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [jobRequestId, token]);

  // The screen is about a job, so it is named rather than identified.
  useEffect(() => {
    let alive = true;
    apiRequest<JobSummary>(`/v1/job-requests/${jobRequestId}`, { token })
      .then((r) => { if (alive) setJob(r); })
      .catch(() => { /* the heading falls back to the id */ });
    return () => { alive = false; };
  }, [jobRequestId, token]);

  if (error && !rows) return <main aria-label="bo-history"><p role="alert">{error}</p></main>;
  if (!rows) return <main aria-label="bo-history"><p>{t('bo.history.loading')}</p></main>;

  return (
    <main aria-label="bo-history">
      <h1>{t('bo.history.title', { id: job?.jobNumber ?? '—' })}</h1>
      {job?.customerName ? <p aria-label="job-summary">{job.customerName}</p> : null}
      <p><Link to={`/bo/timeline/${jobRequestId}`} aria-label="manage-timeline">{t('bo.history.manageTimeline')}</Link></p>
      {error ? <p role="alert">{error}</p> : null}

      {rows.length === 0 ? (
        <p aria-label="empty">{t('bo.history.empty')}</p>
      ) : (
        <table aria-label="history-table">
          <thead><tr><th>{t('bo.history.col.vendor')}</th><th>Event</th><th>{t('bo.history.col.status')}</th><th>{t('bo.history.col.assigned')}</th><th>{t('bo.history.col.reason')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.historyId} aria-label={`row-${r.historyId}`}>
                <td>{personLabel(r.vendorName, r.vendorCode) ?? '—'}</td>
                <td>{r.eventType}</td>
                <td>{r.fromStatus ? `${r.fromStatus} \u2192 ${r.toStatus}` : r.toStatus}</td>
                <td>{r.occurredAt ? at(r.occurredAt) : '\u2014'}</td>
                <td>{r.remarks ?? '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section aria-label="reassign-section">
        {/* Re-assignment needs the new vendor's own work-order line, dates and
            remarks; the assignment page already gathers them and, for a job
            that has been with a vendor, sends them as a re-assignment. */}
        <Link to={`/bo/assignment/${jobRequestId}`} aria-label="reassign-link">{t('bo.history.reassign')}</Link>
      </section>
    </main>
  );
}
